/** Deterministic Northstar calculations; no I/O, limits or production verdicts. */

const CHANNELS = ['R', 'G', 'B'];

export class CalibrationMathError extends Error {
  constructor(message) {
    super(message);
    this.name = 'CalibrationMathError';
  }
}

export class HeadroomError extends CalibrationMathError {
  constructor(message) {
    super(message);
    this.name = 'HeadroomError';
  }
}

function number(value, name = 'value') {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new CalibrationMathError(`${name} must be a finite number`);
  }
  return value;
}

function triple(values, name) {
  if (!Array.isArray(values) || values.length !== 3) {
    throw new CalibrationMathError(`${name} must contain three values`);
  }
  return Array.from(values, value => number(value, name));
}

// Python 3.13's float sum accumulates a compensation term. Keep its operation
// order for matrix products, including cancellation near a measured black.
function sum(values) {
  let high = 0;
  let low = 0;
  for (const value of values) {
    const total = high + value;
    low += Math.abs(high) >= Math.abs(value) ? (high - total) + value : (value - total) + high;
    high = total;
  }
  return low !== 0 && Number.isFinite(low) ? high + low : high;
}

export function xyToXyz(x, y, Y) {
  x = number(x, 'x');
  y = number(y, 'y');
  Y = number(Y, 'Y');
  if (x < 0 || y < 0 || x + y > 1 + 1e-12 || Y < 0) {
    throw new CalibrationMathError('xyY lies outside the physical nonnegative domain');
  }
  if (y === 0) {
    if (x === 0 && Y === 0) return [0, 0, 0];
    throw new CalibrationMathError('Non-black xyY requires y > 0');
  }
  const output = [x / y * Y, Y, Math.max(0, 1 - x - y) / y * Y];
  if (!output.every(Number.isFinite)) throw new CalibrationMathError('xyY conversion overflowed');
  return output;
}

function primaries(primariesRgb) {
  if (!Array.isArray(primariesRgb) || primariesRgb.length !== 3) {
    throw new CalibrationMathError('Exactly three primaries in RGB order are required');
  }
  const result = Array.from(primariesRgb, value => triple(value, 'primary xyY'));
  for (const [x, y, luminance] of result) {
    xyToXyz(x, y, luminance);
    if (y <= 0 || luminance <= 0) throw new CalibrationMathError('Measured primaries require positive y and Y');
  }
  return result;
}

export function primaryMatrix(primariesRgb) {
  const columns = primaries(primariesRgb).map(([x, y]) => xyToXyz(x, y, 1));
  return [0, 1, 2].map(row => columns.map(column => column[row]));
}

function matrixRows(matrix) {
  if (!Array.isArray(matrix) || matrix.length !== 3) throw new CalibrationMathError('A 3x3 matrix is required');
  return Array.from(matrix, row => triple(row, 'matrix row'));
}

export function inverse3(matrix) {
  const original = matrixRows(matrix);
  const norm = Math.max(...original.map(row => sum(row.map(Math.abs))));
  if (norm === 0) throw new CalibrationMathError('Singular primary matrix');
  const work = original.map((row, i) => [...row, ...[0, 1, 2].map(j => Number(i === j))]);
  for (let column = 0; column < 3; column += 1) {
    let pivot = column;
    for (let row = column + 1; row < 3; row += 1) {
      if (Math.abs(work[row][column]) > Math.abs(work[pivot][column])) pivot = row;
    }
    if (Math.abs(work[pivot][column]) <= norm * 1e-14) {
      throw new CalibrationMathError('Singular or ill-conditioned primary matrix');
    }
    [work[column], work[pivot]] = [work[pivot], work[column]];
    const divisor = work[column][column];
    work[column] = work[column].map(value => value / divisor);
    for (let row = 0; row < 3; row += 1) {
      if (row !== column) {
        const factor = work[row][column];
        work[row] = work[row].map((value, index) => value - factor * work[column][index]);
      }
    }
  }
  const result = work.map(row => row.slice(3));
  const inverseNorm = Math.max(...result.map(row => sum(row.map(Math.abs))));
  if (!Number.isFinite(inverseNorm) || norm * inverseNorm > 1e12) {
    throw new CalibrationMathError('Ill-conditioned primary matrix');
  }
  return result;
}

export function mul(matrix, vector) {
  vector = triple(vector, 'vector');
  const output = matrixRows(matrix).map(row => sum(row.map((value, index) => value * vector[index])));
  if (!output.every(Number.isFinite)) throw new CalibrationMathError('Matrix multiplication overflowed');
  return output;
}

export function targets(primariesRgb, targetXyY) {
  const target = triple(targetXyY, 'target xyY');
  if (target[2] <= 0) throw new CalibrationMathError('Target white luminance must be positive');
  const result = mul(inverse3(primaryMatrix(primariesRgb)), xyToXyz(...target));
  if (result.some(value => value <= 0)) throw new CalibrationMathError('Target white is outside the positive primary gamut');
  return result;
}

export function capacity(primariesRgb, targetXyY) {
  const measured = primaries(primariesRgb);
  const requested = targets(measured, targetXyY);
  const result = triple(targetXyY, 'target xyY')[2] * Math.min(...measured.map((primary, index) => primary[2] / requested[index]));
  if (!Number.isFinite(result)) throw new CalibrationMathError('Model capacity overflowed');
  return result;
}

export function headroom(nativePrimaryY, requestedPrimaryY) {
  const native = triple(nativePrimaryY, 'native primary Y');
  const requested = triple(requestedPrimaryY, 'target primary Y');
  if (native.some(value => value < 0) || requested.some(value => value <= 0)) {
    throw new CalibrationMathError('Invalid primary headroom inputs');
  }
  const short = CHANNELS.filter((channel, index) => native[index] < requested[index] - 1e-12 * Math.max(1, native[index], requested[index]));
  if (short.length) throw new HeadroomError(`Insufficient primary headroom: ${short.join(', ')}`);
  const ratios = native.map((value, index) => value / requested[index]);
  if (!ratios.every(Number.isFinite)) throw new CalibrationMathError('Headroom ratio overflowed');
  return ratios;
}

export function chooseBacklight(samples, requestedPrimaryY) {
  if (!samples || typeof samples !== 'object' || Array.isArray(samples) || !Object.prototype.hasOwnProperty.call(samples, '100')) {
    throw new CalibrationMathError('Measured row 100 is required');
  }
  headroom(samples[100], requestedPrimaryY);
  const passing = [];
  for (const [key, measured] of Object.entries(samples)) {
    const row = Number(key);
    if (!/^[1-9][0-9]*$/.test(key) || !Number.isInteger(row) || row < 1 || row > 100) {
      throw new CalibrationMathError('Backlight rows must be integers from 1 to 100');
    }
    try {
      headroom(measured, requestedPrimaryY);
    } catch (error) {
      if (error instanceof HeadroomError) continue;
      throw error;
    }
    passing.push(row);
  }
  return Math.min(...passing);
}

function naturalCoefficients(x, y) {
  const n = x.length;
  const widths = x.slice(1).map((value, index) => value - x[index]);
  const slopes = y.slice(1).map((value, index) => (value - y[index]) / widths[index]);
  const lower = Array(n).fill(0), diagonal = Array(n).fill(1), upper = Array(n).fill(0), rhs = Array(n).fill(0);
  for (let i = 1; i < n - 1; i += 1) {
    lower[i] = widths[i - 1];
    upper[i] = widths[i];
    diagonal[i] = 2 * (widths[i - 1] + widths[i]);
    rhs[i] = 6 * (slopes[i] - slopes[i - 1]);
  }
  for (let i = 1; i < n; i += 1) {
    const factor = lower[i] / diagonal[i - 1];
    diagonal[i] -= factor * upper[i - 1];
    rhs[i] -= factor * rhs[i - 1];
  }
  const second = Array(n).fill(0);
  second[n - 1] = rhs[n - 1] / diagonal[n - 1];
  for (let i = n - 2; i >= 0; i -= 1) second[i] = (rhs[i] - upper[i] * second[i + 1]) / diagonal[i];
  return widths.map((width, i) => [y[i], slopes[i] - width * (2 * second[i] + second[i + 1]) / 6,
    second[i] / 2, (second[i + 1] - second[i]) / (6 * width)]);
}

function endpointSlope(h0, h1, d0, d1) {
  const slope = ((2 * h0 + h1) * d0 - h0 * d1) / (h0 + h1);
  if (slope * d0 <= 0) return 0;
  if (d0 * d1 <= 0 && Math.abs(slope) > 3 * Math.abs(d0)) return 3 * d0;
  return slope;
}

function pchipCoefficients(x, y) {
  const widths = x.slice(1).map((value, index) => value - x[index]);
  const slopes = y.slice(1).map((value, index) => (value - y[index]) / widths[index]);
  const derivatives = Array(x.length).fill(0);
  if (x.length === 2) {
    derivatives.fill(slopes[0]);
  } else {
    derivatives[0] = endpointSlope(widths[0], widths[1], slopes[0], slopes[1]);
    const last = widths.length - 1;
    derivatives[x.length - 1] = endpointSlope(widths[last], widths[last - 1], slopes[last], slopes[last - 1]);
    for (let i = 1; i < x.length - 1; i += 1) {
      const left = slopes[i - 1], right = slopes[i];
      if (left * right > 0) {
        const w1 = 2 * widths[i] + widths[i - 1], w2 = widths[i] + 2 * widths[i - 1];
        derivatives[i] = (w1 + w2) / (w1 / left + w2 / right);
      }
    }
  }
  return widths.map((width, i) => [y[i], derivatives[i], (3 * slopes[i] - 2 * derivatives[i] - derivatives[i + 1]) / width,
    (derivatives[i] + derivatives[i + 1] - 2 * slopes[i]) / width ** 2]);
}

function isMonotone(x, coefficients, scale) {
  const tolerance = 1e-12 * Math.max(1, scale) / Math.max(1, x[x.length - 1] - x[0]);
  return coefficients.every(([, b, c, d], i) => {
    const width = x[i + 1] - x[i];
    const positions = [0, width];
    if (d !== 0) {
      const vertex = -c / (3 * d);
      if (vertex > 0 && vertex < width) positions.push(vertex);
    }
    return !(Math.min(...positions.map(t => b + 2 * c * t + 3 * d * t * t)) < -tolerance);
  });
}

function bisectLeft(values, wanted) {
  let low = 0, high = values.length;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (values[middle] < wanted) low = middle + 1;
    else high = middle;
  }
  return low;
}

function bisectRight(values, wanted) {
  let low = 0, high = values.length;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (wanted < values[middle]) high = middle;
    else low = middle + 1;
  }
  return low;
}

export function sampleCurve(x, y) {
  if (!Array.isArray(x) || !Array.isArray(y) || x.length < 2 || x.length !== y.length || x[0] !== 0 || x[x.length - 1] !== 255
    || Array.from(x).some((value, index) => !Number.isInteger(value) || (index > 0 && value <= x[index - 1]))) {
    throw new CalibrationMathError('Native curve requires increasing integer levels from 0 through 255');
  }
  y = Array.from(y, value => number(value, 'native Y'));
  let coefficients = naturalCoefficients(x, y), method = 'natural_cubic';
  const last = y.length - 1;
  if (!isMonotone(x, coefficients, y[last])) {
    coefficients = pchipCoefficients(x, y);
    method = 'pchip';
  }
  if (!isMonotone(x, coefficients, y[last])) throw new CalibrationMathError('Native interpolation is not monotone');
  let output = Array.from({ length: 256 }, (_, level) => {
    const segment = Math.min(coefficients.length - 1, Math.max(0, bisectRight(x, level) - 1));
    const [a, b, c, d] = coefficients[segment], offset = level - x[segment];
    const value = ((d * offset + c) * offset + b) * offset + a;
    if (!Number.isFinite(value)) throw new CalibrationMathError('Native interpolation overflowed');
    return value;
  });
  x.forEach((level, index) => { output[level] = y[index]; });
  const tolerance = 1e-10 * Math.max(1, y[last]);
  if (output.some((value, index) => index > 0 && value < output[index - 1] - tolerance)) {
    throw new CalibrationMathError('Sampled native interpolation is not monotone');
  }
  output = output.map(value => Math.min(y[last], Math.max(y[0], value)));
  for (let i = 1; i < output.length; i += 1) if (output[i] < output[i - 1]) output[i] = output[i - 1];
  return [output, method];
}

function roundHalfEven(value) {
  const lower = Math.floor(value), fraction = value - lower;
  if (fraction < 0.5) return lower;
  if (fraction > 0.5) return lower + 1;
  return lower % 2 === 0 ? lower : lower + 1;
}

export function inverseCode(native, wanted) {
  if (!Array.isArray(native) || native.length < 2 || Array.from(native).some(value => typeof value !== 'number' || !Number.isFinite(value))
    || native.some((value, index) => index > 0 && value < native[index - 1])) {
    throw new CalibrationMathError('Native inverse requires a finite monotone curve');
  }
  wanted = number(wanted, 'target Y');
  if (wanted <= native[0]) return 0;
  if (wanted > native[native.length - 1]) return native.length - 1;
  const upper = bisectLeft(native, wanted);
  const fractional = upper - 1 + (wanted - native[upper - 1]) / (native[upper] - native[upper - 1]);
  return roundHalfEven(fractional);
}

export function buildLut(levels, XYZramp, primariesRGB, targetxyY, gamma = 2.2, noiseBudget = 0.0) {
  gamma = number(gamma, 'gamma');
  noiseBudget = number(noiseBudget, 'noise budget');
  if (gamma <= 0 || noiseBudget < 0) throw new CalibrationMathError('Gamma must be positive and noise budget nonnegative');
  if (!Array.isArray(levels) || !Array.isArray(XYZramp)) throw new CalibrationMathError('Ramp levels and XYZ must be sequences');
  levels = [...levels];
  XYZramp = [...XYZramp];
  if (levels.length < 3 || levels[0] !== 0 || levels[levels.length - 1] !== 255
    || levels.some(value => !Number.isInteger(value)) || levels.some((value, index) => index > 0 && value <= levels[index - 1])) {
    throw new CalibrationMathError('Ramp requires increasing integer levels from 0 through 255');
  }
  if (XYZramp.length !== levels.length) throw new CalibrationMathError('Ramp levels and measurements differ in length');
  const inv = inverse3(primaryMatrix(primariesRGB));
  const decomposed = XYZramp.map(row => {
    const xyz = triple(row, 'ramp XYZ');
    if (xyz.some(value => value < 0)) throw new CalibrationMathError('Ramp XYZ must be nonnegative');
    return mul(inv, xyz);
  });
  const requested = targets(primariesRGB, targetxyY), ratios = headroom(decomposed[decomposed.length - 1], requested);
  const result = { channels: {}, native: {}, target: {}, interpolation: {}, raw_knots: {}, prepared_knots: {}, noise_repairs: {},
    black_clipping: {}, first_full_output_input: {}, primary_targets: Object.fromEntries(CHANNELS.map((channel, index) => [channel, requested[index]])),
    headroom_ratios: Object.fromEntries(CHANNELS.map((channel, index) => [channel, ratios[index]])), rounding: 'nearest integer; ties to even',
    gamma, noise_budget: noiseBudget, levels };
  CHANNELS.forEach((channel, channelIndex) => {
    const raw = decomposed.map(row => row[channelIndex]), prepared = [], repairs = [];
    const numerical = 1e-12 * Math.max(1, ...raw.map(Math.abs));
    levels.forEach((level, index) => {
      const value = raw[index], adjusted = Math.max(0, value, prepared.length ? prepared[prepared.length - 1] : 0);
      if (adjusted - value > noiseBudget + numerical) throw new CalibrationMathError(`Nonmonotone or negative native ${channel} at input ${level}`);
      if (adjusted !== value) repairs.push({ input: level, raw: value, prepared: adjusted,
        kind: adjusted - value <= numerical ? 'numerical' : 'explicit_noise_budget' });
      prepared.push(adjusted);
    });
    const [native, method] = sampleCurve(levels, prepared);
    const target = Array.from({ length: 256 }, (_, code) => requested[channelIndex] * (code / 255) ** gamma);
    const lut = target.map(wanted => inverseCode(native, wanted));
    const clipped = target.flatMap((wanted, code) => wanted < native[0] ? [code] : []);
    result.channels[channel] = lut;
    result.native[channel] = native;
    result.target[channel] = target;
    result.interpolation[channel] = method;
    result.raw_knots[channel] = raw;
    result.prepared_knots[channel] = prepared;
    result.noise_repairs[channel] = repairs;
    result.black_clipping[channel] = { inputs: clipped, count: clipped.length, last_input: clipped.length ? clipped[clipped.length - 1] : null, native_black: native[0] };
    const firstFull = lut.findIndex(value => value === 255);
    result.first_full_output_input[channel] = firstFull === -1 ? null : firstFull;
  });
  return result;
}

function uvPrime(x, y) {
  x = number(x, 'x');
  y = number(y, 'y');
  if (x < 0 || y < 0 || x + y > 1 + 1e-12) throw new CalibrationMathError('Chromaticity lies outside its physical domain');
  const denominator = -2 * x + 12 * y + 3;
  if (denominator <= 0) throw new CalibrationMathError('Undefined u-prime/v-prime chromaticity');
  return [4 * x / denominator, 9 * y / denominator];
}

export function cieLstar(luminance, referenceLuminance) {
  luminance = number(luminance, 'Y');
  referenceLuminance = number(referenceLuminance, 'reference Y');
  if (luminance < 0 || referenceLuminance <= 0) throw new CalibrationMathError('CIE luminance requires Y >= 0 and reference Y > 0');
  const ratio = luminance / referenceLuminance;
  if (!Number.isFinite(ratio)) throw new CalibrationMathError('CIE luminance ratio overflowed');
  return ratio <= (6 / 29) ** 3 ? (29 / 3) ** 3 * ratio : 116 * ratio ** (1 / 3) - 16;
}

export function ecdisMetrics(measuredXyY, nominalXyY, referenceLuminance, referenceXy = [0.28, 0.31]) {
  const measured = triple(measuredXyY, 'measured xyY'), nominal = triple(nominalXyY, 'nominal xyY');
  if (measured[2] < 0 || nominal[2] < 0) throw new CalibrationMathError('Token luminance cannot be negative');
  xyToXyz(...measured);
  xyToXyz(...nominal);
  if (!Array.isArray(referenceXy) || referenceXy.length !== 2) throw new CalibrationMathError('Reference xy needs two values');
  xyToXyz(...referenceXy, referenceLuminance);
  const referenceUv = uvPrime(...referenceXy), measuredUv = uvPrime(...measured.slice(0, 2)), nominalUv = uvPrime(...nominal.slice(0, 2));
  const lm = cieLstar(measured[2], referenceLuminance), lt = cieLstar(nominal[2], referenceLuminance);
  const [um, vm] = measuredUv.map((value, index) => 13 * lm * (value - referenceUv[index]));
  const [ut, vt] = nominalUv.map((value, index) => 13 * lt * (value - referenceUv[index]));
  const dl = lm - lt, du = um - ut, dv = vm - vt;
  const uvDistance = Math.hypot(measuredUv[0] - nominalUv[0], measuredUv[1] - nominalUv[1]);
  const luminanceRatio = nominal[2] > 0 ? measured[2] / nominal[2] : null;
  if (luminanceRatio !== null && !Number.isFinite(luminanceRatio)) throw new CalibrationMathError('Token luminance ratio overflowed');
  return { delta_uv_star: Math.hypot(du, dv), delta_e_uv: Math.sqrt(dl * dl + du * du + dv * dv), delta_l_star: dl, delta_u_star: du,
    delta_v_star: dv, delta_u_prime_v_prime: uvDistance, luminance_ratio: luminanceRatio, chromaticity_only_delta: 13 * lt * uvDistance,
    measured_lstar: lm, nominal_lstar: lt, reference_xy: [...referenceXy], reference_luminance: referenceLuminance };
}
